import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { MDBContainer, MDBSpinner } from "mdb-react-ui-kit";
import PageWrapper from "../components/Common/PageWrapper";
import { useMessageBox } from "../components/Common/MessageBox";
import { useAuth } from "../context/AuthContext";
import { normalizeRole } from "../utils/rolePermissions";
import "./VendorPayoutsPage.css";

// ─────────────────────────────────────────────────────────────
// NOTE: adjust this to match however your project's other pages
// call the backend (e.g. if ordersApi.js exports a configured
// axios instance, use that instead of raw fetch).
// ─────────────────────────────────────────────────────────────
const API_BASE =
  "https://crosslisting-backend-aacranbzdyd3ffbd.centralindia-01.azurewebsites.net/api/v1";

export const CURRENCY_SYMBOLS = { USD: "$", GBP: "£", EUR: "€", PKR: "Rs" };

const STATUS_OPTIONS = [
  { value: "Delivered", label: "Delivered — final payout" },
  { value: "Dispatched", label: "Dispatched" },
  { value: "approved", label: "QC approved" },
  { value: "picked", label: "Picked" },
  { value: "ready_to_pick", label: "Ready to pick" },
  { value: "Pending", label: "Pending" },
  { value: "Cancelled", label: "Cancelled" },
];

// ── formatting helpers (also used by the receipt page) ──────
export function formatMoney(amount, currency) {
  const num = Number(amount);
  if (!Number.isFinite(num)) return "—";
  const symbol = CURRENCY_SYMBOLS[currency] || "";
  const formatted = num.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return symbol ? `${symbol}${formatted}` : `${formatted} ${currency}`;
}

function formatMoneyMap(moneyMap) {
  const entries = Object.entries(moneyMap || {});
  if (!entries.length) return null;
  return entries;
}

function sumMoneyMapNaive(moneyMap) {
  // Currency-agnostic sum, used ONLY as an internal sort key —
  // never rendered, since mixing currencies isn't a real total.
  return Object.values(moneyMap || {}).reduce((s, v) => s + Number(v || 0), 0);
}

export function formatDisplayOrderNumber(orderId) {
  const id = Number(orderId);
  if (!Number.isFinite(id)) return String(orderId ?? "");
  return String(1000 + id);
}

export function formatDateTime(value) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return String(value);
  }
}

export function monthName(month) {
  const names = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  return names[month - 1] || "";
}

export function periodLabel(period) {
  if (!period) return "";
  const { year, month, half } = period;
  const lastDay = new Date(year, month, 0).getDate();
  const range = half === 1 ? `1–15` : `16–${lastDay}`;
  return `${monthName(month)} ${range}, ${year}`;
}

function shiftPeriod(year, month, half, direction) {
  let y = year;
  let m = month;
  let h = half;
  h += direction;
  if (h > 2) {
    h = 1;
    m += 1;
    if (m > 12) {
      m = 1;
      y += 1;
    }
  } else if (h < 1) {
    h = 2;
    m -= 1;
    if (m < 1) {
      m = 12;
      y -= 1;
    }
  }
  return { year: y, month: m, half: h };
}

function getCurrentPeriod() {
  const now = new Date();
  return {
    year: now.getFullYear(),
    month: now.getMonth() + 1,
    half: now.getDate() <= 15 ? 1 : 2,
  };
}

function escapeCsvValue(value) {
  if (value == null) return '""';
  const str = String(value).replace(/"/g, '""');
  return `"${str}"`;
}

function exportPayoutsToCsv(vendors, status, period) {
  const headers = [
    "Vendor",
    "Order ID",
    "Marketplace",
    status === "Delivered" ? "Delivered At" : "Status Updated At",
    "SKU",
    "Item",
    "Quantity",
    "Selling Price",
    "Amount Payable",
    "Currency",
  ];
  const rows = [];
  vendors.forEach((vendor) => {
    vendor.orders.forEach((order) => {
      order.items.forEach((item) => {
        rows.push([
          vendor.vendor_name,
          formatDisplayOrderNumber(order.order_id),
          order.marketplace,
          formatDateTime(order.status_changed_at),
          item.sku || "",
          item.title || "",
          item.quantity || 1,
          item.price,
          item.amount_payable,
          order.currency,
        ]);
      });
    });
  });
  const csvContent = [
    headers.map(escapeCsvValue).join(","),
    ...rows.map((row) => row.map(escapeCsvValue).join(",")),
  ].join("\n");
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  const suffix = period
    ? `${period.year}-${String(period.month).padStart(2, "0")}-h${period.half}`
    : status.toLowerCase();
  link.setAttribute("download", `vendor_payouts_${suffix}.csv`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

// ── small presentational pieces ─────────────────────────────
// function Money({ amount, currency }) {
//   return <span className="vp-num">{formatMoney(amount, currency)}</span>;
// }

function MoneyMap({ moneyMap }) {
  const entries = formatMoneyMap(moneyMap);
  if (!entries) return <span className="vp-num vp-num-muted">—</span>;
  return (
    <span className="vp-num">
      {entries.map(([cur, amt], i) => (
        <React.Fragment key={cur}>
          {i > 0 && <span className="vp-num-sep">+</span>}
          {formatMoney(amt, cur)}
        </React.Fragment>
      ))}
    </span>
  );
}

function StatusChip({ status }) {
  const map = {
    Delivered: { label: "Final", tone: "positive" },
    Cancelled: { label: "Not payable", tone: "negative" },
  };
  const cfg = map[status] || { label: "Estimate", tone: "neutral" };
  return <span className={`vp-chip vp-chip-${cfg.tone}`}>{cfg.label}</span>;
}

// Average payout per order, per currency — a small "detailing" add
// finance teams find useful (spot vendors with unusually high/low
// per-order value at a glance).
function avgPerOrder(totalPayoutMap, totalOrders) {
  if (!totalOrders) return {};
  const out = {};
  for (const [cur, amt] of Object.entries(totalPayoutMap || {})) {
    out[cur] = Math.round((amt / totalOrders) * 100) / 100;
  }
  return out;
}

export default function VendorPayoutsPage() {
  const { user, companyId } = useAuth();
  const userRole = normalizeRole(user?.role);
  const isAllowed = userRole === "owner" || userRole === "seller manager";
  const navigate = useNavigate();

  const [searchParams, setSearchParams] = useSearchParams();
  const current = getCurrentPeriod();
  const status = searchParams.get("status") || "Delivered";
  const isDeliveredView = status === "Delivered";
  const isCancelledView = status === "Cancelled";
  const year = Number(searchParams.get("year")) || current.year;
  const month = Number(searchParams.get("month")) || current.month;
  const half = Number(searchParams.get("half")) || current.half;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [vendorSearch, setVendorSearch] = useState("");
  const [sortKey, setSortKey] = useState("payout"); // "vendor" | "orders" | "items" | "payout"
  const [sortDir, setSortDir] = useState("desc");

  const { showError, MessageBoxComponent } = useMessageBox();
  const period = { year, month, half };

  const setStatus = (newStatus) => {
    if (newStatus === "Delivered") {
      setSearchParams({ status: newStatus, year, month, half });
    } else {
      setSearchParams({ status: newStatus });
    }
  };

  const setPeriod = (p) => {
    setSearchParams({ status, year: p.year, month: p.month, half: p.half });
  };

  const goPrev = () => setPeriod(shiftPeriod(year, month, half, -1));
  const goNext = () => setPeriod(shiftPeriod(year, month, half, 1));
  const goCurrent = () => setPeriod(getCurrentPeriod());

  const isCurrentPeriod =
    year === current.year && month === current.month && half === current.half;

  const loadPayouts = useCallback(async () => {
    if (!companyId || !isAllowed) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({
        company_id: companyId,
        role: userRole,
        status,
      });
      if (isDeliveredView) {
        params.set("year", year);
        params.set("month", month);
        params.set("half", half);
      }
      const res = await fetch(
        `${API_BASE}/orders/dashboard/vendor-payouts?${params.toString()}`,
      );
      const json = await res.json();
      if (!res.ok || !json.success) {
        showError(json.error || "Failed to load payout report");
        setData(null);
        return;
      }
      setData(json);
    } catch (err) {
      showError(err.message || "Failed to load payout report");
      setData(null);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    companyId,
    isAllowed,
    userRole,
    status,
    isDeliveredView,
    year,
    month,
    half,
  ]);

  useEffect(() => {
    loadPayouts();
  }, [loadPayouts]);

  const vendors = useMemo(() => data?.vendors || [], [data]);

  const kpis = useMemo(() => {
    const totalOrders = vendors.reduce((s, v) => s + v.total_orders, 0);
    const totalItems = vendors.reduce((s, v) => s + v.total_items, 0);
    return {
      vendorCount: vendors.length,
      totalOrders,
      totalItems,
      grandTotal: data?.grand_total || {},
    };
  }, [vendors, data]);

  const visibleVendors = useMemo(() => {
    let list = vendors;
    const q = vendorSearch.trim().toLowerCase();
    if (q) {
      list = list.filter((v) => v.vendor_name.toLowerCase().includes(q));
    }
    const sorted = [...list].sort((a, b) => {
      let cmp = 0;
      if (sortKey === "vendor")
        cmp = a.vendor_name.localeCompare(b.vendor_name);
      else if (sortKey === "orders") cmp = a.total_orders - b.total_orders;
      else if (sortKey === "items") cmp = a.total_items - b.total_items;
      else
        cmp =
          sumMoneyMapNaive(a.total_payout) - sumMoneyMapNaive(b.total_payout);
      return sortDir === "asc" ? cmp : -cmp;
    });
    return sorted;
  }, [vendors, vendorSearch, sortKey, sortDir]);

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir(key === "vendor" ? "asc" : "desc");
    }
  };

  const openReceipt = (vendorId) => {
    const params = new URLSearchParams({ status });
    if (isDeliveredView) {
      params.set("year", year);
      params.set("month", month);
      params.set("half", half);
    }
    navigate(`/vendor-payouts/receipt/${vendorId}?${params.toString()}`);
  };

  const handleExport = () => {
    if (!vendors.length) {
      showError("No payout data to export for this view");
      return;
    }
    exportPayoutsToCsv(vendors, status, isDeliveredView ? period : null);
  };

  if (!isAllowed) {
    return (
      <MDBContainer fluid className="py-4">
        <PageWrapper title="Vendor Payouts" description="">
          <div className="vp-denied">
            This report is restricted to Owner and Seller Manager roles.
          </div>
        </PageWrapper>
      </MDBContainer>
    );
  }

  const sortArrow = (key) =>
    sortKey === key ? (sortDir === "asc" ? "↑" : "↓") : "";

  return (
    <div className="vp-root">
      <MDBContainer fluid className="vp-container">
        <header className="vp-header">
          <div>
            <p className="vp-eyebrow">Finance</p>
            <h1 className="vp-title">Vendor payouts</h1>
            <p className="vp-subtitle">
              What each vendor is owed, grouped by fulfillment status and 15-day
              cycle.
            </p>
          </div>
        </header>

        <div className="vp-toolbar">
          <div className="vp-toolbar-group">
            <label className="vp-field">
              <span className="vp-field-label">Status</span>
              <select
                className="vp-select"
                value={status}
                onChange={(e) => setStatus(e.target.value)}
              >
                {STATUS_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </label>

            <label className="vp-field">
              <span className="vp-field-label">Vendor</span>
              <input
                className="vp-input"
                type="text"
                placeholder="Search vendor…"
                value={vendorSearch}
                onChange={(e) => setVendorSearch(e.target.value)}
              />
            </label>
          </div>

          <div className="vp-toolbar-group vp-toolbar-group-right">
            {isDeliveredView ? (
              <div className="vp-period-nav">
                <button
                  type="button"
                  className="vp-icon-btn"
                  onClick={goPrev}
                  aria-label="Previous period"
                >
                  ‹
                </button>
                <span className="vp-period-label">
                  {periodLabel(period)}
                  {isCurrentPeriod && (
                    <span className="vp-chip vp-chip-positive vp-chip-sm">
                      Current
                    </span>
                  )}
                </span>
                <button
                  type="button"
                  className="vp-icon-btn"
                  onClick={goNext}
                  aria-label="Next period"
                >
                  ›
                </button>
                {!isCurrentPeriod && (
                  <button
                    type="button"
                    className="vp-link-btn"
                    onClick={goCurrent}
                  >
                    Jump to current
                  </button>
                )}
              </div>
            ) : (
              <div
                className={`vp-mode-note ${isCancelledView ? "vp-mode-note-negative" : ""}`}
              >
                {isCancelledView
                  ? "Cancelled orders — shown for reference, not payable."
                  : "Not yet delivered — amounts are estimates."}
              </div>
            )}

            <button
              type="button"
              className="vp-btn-primary"
              onClick={handleExport}
              disabled={loading || !vendors.length}
            >
              Export CSV
            </button>
          </div>
        </div>

        <div className="vp-kpi-row">
          <div className="vp-kpi-card">
            <span className="vp-kpi-label">
              {isCancelledView ? "Total (not payable)" : "Total payout"}
            </span>
            <span className="vp-kpi-value">
              {isCancelledView ? (
                <span className="vp-num vp-num-muted">—</span>
              ) : (
                <MoneyMap moneyMap={kpis.grandTotal} />
              )}
            </span>
          </div>
          <div className="vp-kpi-card">
            <span className="vp-kpi-label">Vendors</span>
            <span className="vp-kpi-value vp-num">{kpis.vendorCount}</span>
          </div>
          <div className="vp-kpi-card">
            <span className="vp-kpi-label">Orders</span>
            <span className="vp-kpi-value vp-num">{kpis.totalOrders}</span>
          </div>
          <div className="vp-kpi-card">
            <span className="vp-kpi-label">Items</span>
            <span className="vp-kpi-value vp-num">{kpis.totalItems}</span>
          </div>
        </div>

        <div className="vp-table-card">
          {loading ? (
            <div className="vp-state">
              <MDBSpinner role="status" />
              <span>Loading payout report…</span>
            </div>
          ) : !vendors.length ? (
            <div className="vp-state vp-state-empty">
              <p className="vp-state-title">No orders found for this view</p>
              <p className="vp-state-body">
                Try a different status
                {isDeliveredView ? " or move to another period" : ""}.
              </p>
            </div>
          ) : (
            <table className="vp-table">
              <thead>
                <tr>
                  <th
                    className="vp-sortable"
                    onClick={() => toggleSort("vendor")}
                  >
                    Vendor{" "}
                    <span className="vp-sort-arrow">{sortArrow("vendor")}</span>
                  </th>
                  <th
                    className="vp-sortable"
                    onClick={() => toggleSort("orders")}
                  >
                    Orders{" "}
                    <span className="vp-sort-arrow">{sortArrow("orders")}</span>
                  </th>
                  <th
                    className="vp-sortable"
                    onClick={() => toggleSort("items")}
                  >
                    Items{" "}
                    <span className="vp-sort-arrow">{sortArrow("items")}</span>
                  </th>
                  <th className="">Avg / order</th>
                  <th
                    className="vp-sortable"
                    onClick={() => toggleSort("payout")}
                  >
                    {isCancelledView ? "Status" : "Payout"}{" "}
                    <span className="vp-sort-arrow">{sortArrow("payout")}</span>
                  </th>
                  <th className="vp-th-action" />
                </tr>
              </thead>
              <tbody>
                {visibleVendors.map((vendor) => (
                  <tr
                    key={vendor.vendor_id}
                    className="vp-row"
                    onClick={() => openReceipt(vendor.vendor_id)}
                  >
                    <td className="vp-vendor-cell">
                      <span className="vp-vendor-name">
                        {vendor.vendor_name}
                      </span>
                      {(vendor.vendor_store || vendor.vendor_phone) && (
                        <span className="vp-vendor-sub">
                          {[vendor.vendor_store, vendor.vendor_phone]
                            .filter(Boolean)
                            .join(" · ")}
                        </span>
                      )}
                    </td>
                    <td className="vp-num">{vendor.total_orders}</td>
                    <td className="vp-num">{vendor.total_items}</td>
                    <td className="">
                      {isCancelledView ? (
                        <span className="vp-num vp-num-muted">—</span>
                      ) : (
                        <MoneyMap
                          moneyMap={avgPerOrder(
                            vendor.total_payout,
                            vendor.total_orders,
                          )}
                        />
                      )}
                    </td>
                    <td className="">
                      {isCancelledView ? (
                        <StatusChip status="Cancelled" />
                      ) : (
                        <MoneyMap moneyMap={vendor.total_payout} />
                      )}
                    </td>
                    <td className="vp-th-action">
                      <button
                        type="button"
                        className="vp-receipt-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          openReceipt(vendor.vendor_id);
                        }}
                      >
                        View receipt →
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <MessageBoxComponent />
      </MDBContainer>
    </div>
  );
}
